import { NOTIFICATION_TAB_UPDATES, NOTIFICATION_TAB_MESSAGES } from '@constants/notification';
import { NotificationTab } from '@interfaces/models/notification';
import { TFunction } from 'next-i18next';

interface NotificationBoxStylesParams {
  notificationsBox: HTMLElement | null;
  previewInnerBox: HTMLElement | null;
  isWiderThanLg: boolean;
  isWiderThanXs: boolean;
}

export const getElementHeight = (className: string, fallbackHeight?: number): number => {
  const element = document.getElementsByClassName(className)[0] as HTMLElement;
  return element?.offsetHeight || (fallbackHeight !== undefined ? fallbackHeight : 0);
};

export const getBannerStyles = (): { primaryBannerHeight: number } => {
  const accessibilityTopLinkHeight = getElementHeight('accessibilityTopLink');
  const vcBannerContainerHeight = getElementHeight('vc-banner__container');
  const primaryBannerHeight = Math.max(accessibilityTopLinkHeight, vcBannerContainerHeight);

  return {
    primaryBannerHeight,
  };
};

export const setNotificationBoxStyles = ({
  notificationsBox,
  previewInnerBox,
  isWiderThanLg,
  isWiderThanXs,
}: NotificationBoxStylesParams): void => {
  const { primaryBannerHeight } = getBannerStyles();
  // TODO: Fallback heights for lazy-loaded components
  const notificationsTabListHeight = getElementHeight('notificationsTabList', 43);
  const notificationsHeaderHeight = getElementHeight('notificationsHeader', 61);

  if (notificationsBox && previewInnerBox) {
    const ncTabHeaderMergedHeight = notificationsTabListHeight + notificationsHeaderHeight;
    if (isWiderThanLg) {
      const calculatedHeight = `calc(100vh - ${ncTabHeaderMergedHeight + primaryBannerHeight + 25}px)`;
      notificationsBox.style.maxHeight = calculatedHeight;
      notificationsBox.style.height = calculatedHeight;
      previewInnerBox.style.maxHeight = `calc(100vh - ${primaryBannerHeight}px)`;
    } else if (isWiderThanXs) {
      notificationsBox.style.maxHeight = `calc(100vh - ${ncTabHeaderMergedHeight + 7}px)`;
      notificationsBox.style.height = `calc(100vh - ${ncTabHeaderMergedHeight + 30}px)`;
    }
  }
};

export const isSelectedUpdatesTab = (selected: NotificationTab): boolean => selected === NOTIFICATION_TAB_UPDATES;
export const isSelectedMessagesTab = (selected: NotificationTab): boolean => selected === NOTIFICATION_TAB_MESSAGES;

export const getNotificationsDataLength = (isUpdatesSelected, updatesState, messagesState): number | undefined =>
  isUpdatesSelected ? updatesState?.data?.length : messagesState?.data?.length;

export const isUpdatesOrMessagesEmpty = (dataLength: number, isOnline: boolean): boolean =>
  dataLength === 0 || !isOnline;

export const calculateTimeDifference = (utcTimeFrom: string, t: TFunction): string => {
  const from = new Date(utcTimeFrom);
  const to = new Date();

  const diffInMilliseconds = Math.abs(to.getTime() - from.getTime());

  const seconds = Math.floor(diffInMilliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);

  const years = Math.floor(weeks / 52);
  const months = Math.floor(years * 12 + (weeks % 52) / 4.345); // Approximate conversion

  if (years > 0) {
    return `${years}${t('NOTIFICATION_CENTER_SCREEN_FORMATTED_TIME_YEAR')}`;
  } else if (months > 0) {
    return `${months}${t('NOTIFICATION_CENTER_SCREEN_FORMATTED_TIME_MONTH')}`;
  } else if (weeks > 0) {
    return `${weeks}${t('NOTIFICATION_CENTER_SCREEN_FORMATTED_TIME_WEEK')}`;
  } else if (days > 0) {
    return `${days}${t('NOTIFICATION_CENTER_SCREEN_FORMATTED_TIME_DAY')}`;
  } else if (hours > 0) {
    return `${hours}${t('NOTIFICATION_CENTER_SCREEN_FORMATTED_TIME_HOUR')}`;
  } else if (minutes > 0) {
    return `${minutes}${t('NOTIFICATION_CENTER_SCREEN_FORMATTED_TIME_MINUTE')}`;
  } else if (seconds > 0) {
    return `${seconds}${t('NOTIFICATION_CENTER_SCREEN_FORMATTED_TIME_SECOND')}`;
  } else {
    return t('NOTIFICATION_CENTER_SCREEN_FORMATTED_TIME_NOW');
  }
};

export const constructNotificationTrackingPayload = (notification, selectedTab) => {
  const tabSection =
    selectedTab === 'updates'
      ? `${selectedTab}_${notification.category === 'important' ? 'important' : 'all'}`
      : 'messages';

  return {
    id: notification.id,
    type: `${notification.id}_${notification.eventType}`,
    screen_section: tabSection,
    read_status: notification.status === 'read' ? 1 : 0,
    position: notification.position,
  };
};
